import { useCallback, useDeferredValue, useEffect, useMemo, useRef, startTransition, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Skeleton, Tabs, TabsList } from "@nexus/ui";
import { Clock, Loader2, Trash2, XCircle } from "lucide-react";
import { Sentry } from "@/lib/sentry";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import {
  useIamUsers,
  useIamTenants,
  useTenantPicker,
  useIamAccess,
  useAccessGroupsForTenants,
  useAllUsersAccess,
  useReactivateUser,
  useRevokeAccess,
  useGrantAccess,
  useDeleteAccessGroup,
  useBulkRevokeAccess,
  useIamInvites,
  useResendInvite,
  useRegenerateInvite,
  useInviteLink,
  useRevokeInvite,
} from "@/features/identity-hub/hooks/useIdentityHub";
import {
  flattenInfinitePages,
  toIamAccessListFilters,
  toIamTenantsListFilters,
  toIamUsersListFilters,
  toInvitesListFilters,
  mapIamAccessToRoleRow,
  sortRoleRows,
  DEFAULT_ROLE_SORT,
} from "@/features/identity-hub/lib/iam-list-filters";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { useIamDashboardStore, type IamDashTab } from "@/features/identity-hub/store/identity-hub-dashboard.store";
import { identityHubKeys } from "@/lib/queries";
import { grantUserAccess } from "@/features/identity-hub/api/identity-hub.api";
import { IamKpiStrip } from "./IamKpiStrip";
import { PeopleTable, type PeopleFilters, type SortKey, type SortState } from "./PeopleTable";
import { PeopleToolbar } from "./PeopleToolbar";
import { BulkGrantDialog } from "./BulkGrantDialog";
import { PersonDrawer } from "./PersonDrawer";
import { industryLabel } from "./OrgCard";
import { OrgToolbar, type OrgFilters } from "./OrgToolbar";
import { OrgDrawer } from "./OrgDrawer";
import { OrgSettingsModal } from "./OrgSettingsModal";
import { OrgsTable, type OrgSortKey, type OrgSortState } from "./OrgsTable";
import { buildBrandColorFilterOptions, tenantBrandColorFilterKey } from "@/features/identity-hub/lib/brand-colors";
import { RolesToolbar, type RoleFilters } from "./RolesToolbar";
import { RolesTable, type RoleSortKey, type RoleSortState } from "./RolesTable";
import { RoleDrawer } from "./RoleDrawer";
import { EditPermissionsDialog } from "./EditPermissionsDialog";
import { NewRoleDialog } from "./NewRoleDialog";
import { NewOrgDialog } from "./NewOrgDialog";
import { InvitePersonDialog } from "./InvitePersonDialog";
import { EditPersonDialog } from "./EditPersonDialog";
import { SuspendPersonDialog } from "./SuspendPersonDialog";
import { AuditLogModal, type AuditLogModalProps } from "./AuditLogModal";
import { InvitesToolbar, type InvitesFilters } from "./InvitesToolbar";
import { InvitesTable, type InviteSortKey, type InviteSortState } from "./InvitesTable";
import type { UserItem, TenantListItem, InviteListItem } from "@/features/identity-hub/api/identity-hub.api";
import { buildInviteUrl } from "@/features/auth/lib/invite";

const TAB_OPTION_VALUES: IamDashTab[] = ["people", "roles", "orgs", "invites"];
const IAM_STATS_WINDOW = "7d" as const;

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function IdentityHubDashboard() {
  const { t } = useTranslation();
  const { activeTab, setActiveTab } = useIamDashboardStore();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const loggedInEmail = useAuth((s) => s.user?.email);
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const canNewOrg = perms.IDENTITY_HUB_CREATE_TENANT.isOn || isSuperTenant;
  const canInvite = perms.IDENTITY_HUB_GENERATE_INVITE.isOn || isSuperTenant;
  const canViewInvites = perms.IDENTITY_HUB_LIST_INVITES.isOn || isSuperTenant;
  const canNewRole = perms.IDENTITY_HUB_CREATE_ACCESS.isOn || isSuperTenant;
  const canEditPermissions = perms.IDENTITY_HUB_UPDATE_PERMISSIONS.isOn || isSuperTenant;
  const canDeleteRole = perms.IDENTITY_HUB_DELETE_ACCESS.isOn || isSuperTenant;
  const canViewAuditLog = perms.IDENTITY_HUB_VIEW_AUDIT_LOG.isOn || isSuperTenant;
  // Per-action gates for the invite row menu — mirror the backend's
  // requirePermission routes so the "…" items match what the caller may do
  // (super-tenants bypass, matching the API's SUPER_TENANT bypass).
  const canCopyInviteLink   = perms.IDENTITY_HUB_GET_INVITE_LINK.isOn   || isSuperTenant;
  const canResendInvite     = perms.IDENTITY_HUB_RESEND_INVITE.isOn     || isSuperTenant;
  const canRegenerateInvite = perms.IDENTITY_HUB_REGENERATE_INVITE.isOn || isSuperTenant;
  const canRevokeInvite     = perms.IDENTITY_HUB_REVOKE_INVITE.isOn     || isSuperTenant;

  // ── People tab state ──────────────────────────────────────────────
  const defaultPeopleOrgId = isSuperTenant ? null : activeTenantId;
  const [peopleFilters, setPeopleFilters] = useState<PeopleFilters>({
    search: "", orgId: defaultPeopleOrgId, role: null, status: null, noRole: false, neverSignedIn: false, hasPendingInvite: false,
  });

  useEffect(() => {
    if (isSuperTenant) return;
    setPeopleFilters((prev) => ({ ...prev, orgId: activeTenantId }));
  }, [activeTenantId, isSuperTenant]);
  const [peopleSort, setPeopleSort] = useState<SortState>({ key: "name", dir: "asc" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeUserId, setActiveUserId] = useState<string | null>(null);

  // ── Orgs tab state ────────────────────────────────────────────────
  const [orgFilters, setOrgFilters] = useState<OrgFilters>({ search: "", industry: null, size: null, showArchived: false, excludedColors: [] });
  const [orgSort, setOrgSort] = useState<OrgSortState>({ key: "name", dir: "asc" });
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [settingsOrgId, setSettingsOrgId] = useState<string | null>(null);

  // ── Roles tab state ───────────────────────────────────────────────
  const [roleFilters, setRoleFilters] = useState<RoleFilters>({ search: "", orgId: null, type: null, showArchived: false, onlyMine: false });
  const [roleSort, setRoleSort] = useState<RoleSortState>(DEFAULT_ROLE_SORT);
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);

  // ── Invites tab state ─────────────────────────────────────────────
  const [invitesFilters, setInvitesFilters] = useState<InvitesFilters>({
    search: "", status: "all", tenantId: null,
  });
  const [invitesSort, setInvitesSort] = useState<InviteSortState>({ key: "createdAt", dir: "desc" });
  const onInvitesSort = useCallback((key: InviteSortKey) => {
    setInvitesSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }, []);
  const [revokingInvite, setRevokingInvite] = useState<InviteListItem | null>(null);
  const onInvitesFilterChange = <K extends keyof InvitesFilters>(key: K, value: InvitesFilters[K]) => {
    setInvitesFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ── Data (server-paginated People / Orgs / Invites / Roles) ───────
  const deferredPeopleSearch = useDeferredValue(peopleFilters.search);
  const deferredOrgSearch = useDeferredValue(orgFilters.search);
  const deferredInviteSearch = useDeferredValue(invitesFilters.search);
  const deferredRoleSearch = useDeferredValue(roleFilters.search);

  const peopleApiFilters = useMemo(
    () => toIamUsersListFilters(peopleFilters, peopleSort, deferredPeopleSearch),
    [peopleFilters, peopleSort, deferredPeopleSearch],
  );
  const tenantsApiFilters = useMemo(
    () => toIamTenantsListFilters(orgFilters, {
      includeArchived: isSuperTenant,
      searchOverride: deferredOrgSearch,
      sort: orgSort,
    }),
    [orgFilters, isSuperTenant, deferredOrgSearch, orgSort],
  );
  const orgsActiveCountFilters = useMemo(
    () => toIamTenantsListFilters(orgFilters, {
      includeArchived: false,
      searchOverride: deferredOrgSearch,
    }),
    [orgFilters, deferredOrgSearch],
  );
  const invitesApiFilters = useMemo(
    () => toInvitesListFilters(invitesFilters, deferredInviteSearch, invitesSort),
    [invitesFilters, deferredInviteSearch, invitesSort],
  );
  const rolesApiFilters = useMemo(
    () => toIamAccessListFilters(roleFilters, {
      includeArchived: isSuperTenant && roleFilters.showArchived,
      searchOverride: deferredRoleSearch,
      sort: roleSort,
    }),
    [roleFilters, isSuperTenant, deferredRoleSearch, roleSort],
  );
  const rolesAllCountFilters = useMemo(
    () => toIamAccessListFilters(roleFilters, {
      includeArchived: true,
      searchOverride: deferredRoleSearch,
    }),
    [roleFilters, deferredRoleSearch],
  );
  const rolesActiveCountFilters = useMemo(
    () => toIamAccessListFilters(roleFilters, {
      includeArchived: false,
      searchOverride: deferredRoleSearch,
    }),
    [roleFilters, deferredRoleSearch],
  );

  const peopleQuery = useIamUsers(peopleApiFilters);
  const tenantsQuery = useIamTenants(tenantsApiFilters);
  const tenantPickerQuery = useTenantPicker();
  const orgsActiveCountQuery = useIamTenants(orgsActiveCountFilters, {
    enabled: isSuperTenant,
  });
  const invitesQuery = useIamInvites(invitesApiFilters, { enabled: canViewInvites });
  // /access/all is a super-tenant-only cross-tenant endpoint; enterprise admins use
  // per-tenant data from groupsByTenant instead to avoid a 403.
  const rolesQuery = useIamAccess(rolesApiFilters, { enabled: isSuperTenant });
  const rolesAllCountQuery = useIamAccess(rolesAllCountFilters, {
    enabled: isSuperTenant && !roleFilters.showArchived,
  });
  const rolesActiveCountQuery = useIamAccess(rolesActiveCountFilters, {
    enabled: isSuperTenant && roleFilters.showArchived,
  });
  // Role drawer members: server-side, paginated by accessId (the true membership,
  // not just the users on the loaded People page). Only fetched while a role
  // drawer is open.
  const roleMembersQuery = useIamUsers(
    { accessId: activeRoleId ?? undefined },
    { enabled: activeTab === "roles" && !!activeRoleId },
  );

  const users = useMemo(
    () => flattenInfinitePages<UserItem>(peopleQuery.data?.pages),
    [peopleQuery.data],
  );
  const allTenants = useMemo(
    () => flattenInfinitePages<TenantListItem>(tenantsQuery.data?.pages),
    [tenantsQuery.data],
  );
  const allInvites = useMemo(
    () => flattenInfinitePages<InviteListItem>(invitesQuery.data?.pages),
    [invitesQuery.data],
  );

  // `status` and `sentBy` can't be ordered by the listing API, so sort the
  // loaded rows here by name. Server-supported keys arrive pre-sorted.
  const sortedInvites = useMemo(() => {
    if (invitesSort.key !== "status" && invitesSort.key !== "sentBy") return allInvites;
    const factor = invitesSort.dir === "asc" ? 1 : -1;
    const keyOf = (inv: InviteListItem) =>
      invitesSort.key === "status"
        ? inv.status
        : inv.createdByName ?? inv.createdByEmail ?? "System";
    return [...allInvites].sort(
      (a, b) => factor * keyOf(a).localeCompare(keyOf(b), undefined, { sensitivity: "base" }),
    );
  }, [allInvites, invitesSort]);
  const roleRowsFromApi = useMemo(
    () => flattenInfinitePages(rolesQuery.data?.pages).map(mapIamAccessToRoleRow),
    [rolesQuery.data],
  );

  const tenants = useMemo(() => allTenants.filter((t) => !t.deletedAt), [allTenants]);
  const filteredTenants = useMemo(
    () => (orgFilters.showArchived ? allTenants : tenants),
    [orgFilters.showArchived, allTenants, tenants],
  );

  // ── Color filter derived data ─────────────────────────────────────
  const availableColors = useMemo(
    () => buildBrandColorFilterOptions(filteredTenants),
    [filteredTenants],
  );

  const colorFilteredTenants = useMemo(() => {
    if (orgFilters.excludedColors.length === 0) return filteredTenants;
    const available = new Set(availableColors.map((c) => c.value));
    const excluded = new Set(orgFilters.excludedColors.filter((c) => available.has(c)));
    if (excluded.size === 0) return filteredTenants;
    return filteredTenants.filter((t) => {
      const key = tenantBrandColorFilterKey(t);
      if (!key) return true;
      return !excluded.has(key);
    });
  }, [filteredTenants, orgFilters.excludedColors, availableColors]);

  const usersLoading = peopleQuery.isLoading;
  const tenantsLoading = tenantsQuery.isLoading;

  // Invite / add-org pickers hide tenants that have no loaded roles
  // (InviteOrgRoleList, PersonDrawer). Groups must cover every picker
  // tenant, not only the Organizations-tab page currently on screen.
  const pickerTenantIds = useMemo(
    () => (tenantPickerQuery.data?.items ?? []).filter((t) => !t.deletedAt).map((t) => t.id),
    [tenantPickerQuery.data],
  );
  const tenantIds = useMemo(
    () => (pickerTenantIds.length > 0 ? pickerTenantIds : allTenants.map((t) => t.id)),
    [pickerTenantIds, allTenants],
  );
  const { groupsByTenant } = useAccessGroupsForTenants(tenantIds, { includeArchived: isSuperTenant });

  const activeRoleMembers = useMemo(
    () => flattenInfinitePages<UserItem>(roleMembersQuery.data?.pages),
    [roleMembersQuery.data],
  );

  // Include the open role's members so their access trees (super-tenant badge,
  // org membership) resolve even when they're outside the loaded People page.
  const userIds = useMemo(
    () => [...new Set([...users.map((u) => u.id), ...activeRoleMembers.map((m) => m.id)])],
    [users, activeRoleMembers],
  );
  const { accessByUser } = useAllUsersAccess(userIds);

  const allAccessGroups = useMemo(
    () => [...groupsByTenant.values()].flat(),
    [groupsByTenant],
  );

  const activeUsers = useMemo(() => users.filter((u) => u.status === "ACTIVE"), [users]);
  const pendingUsers = useMemo(() => users.filter((u) => u.status === "PENDING"), [users]);
  const deactivatedUsers = useMemo(() => users.filter((u) => u.status === "DEACTIVATED"), [users]);
  const currentUserId = useMemo(
    () => users.find((user) => user.email === loggedInEmail)?.id ?? null,
    [loggedInEmail, users],
  );

  const neverLoggedIn = useMemo(
    () => activeUsers.filter((u) => !u.lastSeenAt).length,
    [activeUsers],
  );

  const rolelessCount = useMemo(
    () => activeUsers.filter((u) => !(accessByUser.get(u.id)?.length)).length,
    [activeUsers, accessByUser],
  );

  const loading = usersLoading || tenantsLoading;

  const peopleTotal = peopleQuery.data?.pages[0]?.totalCount;
  const orgsAllTotal = tenantsQuery.data?.pages[0]?.totalCount;
  const orgsActiveTotal = isSuperTenant
    ? orgsActiveCountQuery.data?.pages[0]?.totalCount
    : orgsAllTotal;
  const archivedTenantCount = isSuperTenant && orgsAllTotal != null && orgsActiveTotal != null
    ? Math.max(orgsAllTotal - orgsActiveTotal, 0)
    : undefined;
  const orgsTotal = isSuperTenant && !orgFilters.showArchived
    ? orgsActiveTotal
    : orgsAllTotal;
  const rolesVisibleTotal = rolesQuery.data?.pages[0]?.totalCount ?? undefined;
  const rolesAllTotal = isSuperTenant && !roleFilters.showArchived
    ? rolesAllCountQuery.data?.pages[0]?.totalCount ?? undefined
    : rolesVisibleTotal;
  const rolesActiveTotal = isSuperTenant
    ? roleFilters.showArchived
      ? rolesActiveCountQuery.data?.pages[0]?.totalCount ?? undefined
      : rolesVisibleTotal
    : rolesVisibleTotal;
  const archivedRoleCount = isSuperTenant && rolesAllTotal != null && rolesActiveTotal != null
    ? Math.max(rolesAllTotal - rolesActiveTotal, 0)
    : undefined;
  const rolesTotal = rolesVisibleTotal;
  const invitesPendingTotal = invitesQuery.data?.pages[0]?.statusCounts?.pending;

  const inviteStatusCounts = useMemo(() => {
    const server = invitesQuery.data?.pages[0]?.statusCounts;
    if (server) {
      return {
        all: server.all ?? 0,
        pending: server.pending ?? 0,
        expired: server.expired ?? 0,
        accepted: server.accepted ?? 0,
      };
    }
    const counts = { all: allInvites.length, pending: 0, expired: 0, accepted: 0 };
    for (const inv of allInvites) counts[inv.status]++;
    return counts;
  }, [invitesQuery.data, allInvites]);
  const orgLogosByTenantId = useMemo(
    () => new Map(tenants.map((t) => [t.id, t.logoUrl ?? null])),
    [tenants],
  );

  // The auth-store tenant snapshot (powering the nav tenant switcher) only carries
  // logo URLs as of the last token refresh, so newly uploaded logos don't appear in
  // the switcher. The IAM org list is the freshest source — mirror its logo URLs back
  // into the auth store so the switcher matches the org cards.
  const syncAuthTenants = useAuth((s) => s.updateTenants);
  useEffect(() => {
    if (orgLogosByTenantId.size === 0) return;
    const authTenants = useAuth.getState().tenants;
    let changed = false;
    const next = authTenants.map((tenant) => {
      if (!orgLogosByTenantId.has(tenant.id)) return tenant;
      const freshLogoUrl = orgLogosByTenantId.get(tenant.id) ?? null;
      if ((tenant.logoUrl ?? null) === freshLogoUrl) return tenant;
      changed = true;
      return { ...tenant, logoUrl: freshLogoUrl };
    });
    if (changed) syncAuthTenants(next);
  }, [orgLogosByTenantId, syncAuthTenants]);
  const resendInvite = useResendInvite();
  const regenerateInviteMut = useRegenerateInvite();
  const inviteLinkMut = useInviteLink();
  const revokeInviteMut = useRevokeInvite();
  const busyInviteId = resendInvite.isPending
    ? resendInvite.variables?.id ?? null
    : inviteLinkMut.isPending
      ? inviteLinkMut.variables ?? null
      : regenerateInviteMut.isPending
        ? null
        : revokeInviteMut.isPending
          ? revokeInviteMut.variables ?? null
          : null;
  const handleResendInvite = (invite: InviteListItem) => {
    resendInvite.mutate({ id: invite.id });
  };
  const handleRegenerateInvite = (invite: InviteListItem) => {
    regenerateInviteMut.mutate({ email: invite.email });
  };
  // Write the invite link to the clipboard and confirm with a toast (or warn
  // if the clipboard write fails / is unavailable).
  const copyInviteLinkToClipboard = useCallback(async (token: string) => {
    try {
      if (!navigator.clipboard) throw new Error("clipboard-unavailable");
      await navigator.clipboard.writeText(buildInviteUrl(token));
      toast.success(t("iam.toast.inviteLinkCopied", "Invite link copied to clipboard"));
    } catch {
      toast.error(t("iam.toast.inviteLinkCopyFailed", "Couldn’t copy to clipboard — please copy it manually."));
    }
  }, [t]);
  // Copy the EXISTING link (read-only) — must NOT regenerate, which would
  // expire the link already emailed to the invitee (AD-1714).
  const handleCopyInviteLink = (invite: InviteListItem) => {
    inviteLinkMut.mutate(invite.id, {
      onSuccess: (data) => { void copyInviteLinkToClipboard(data.token); },
    });
  };
  const handleConfirmRevoke = () => {
    if (!revokingInvite) return;
    revokeInviteMut.mutate(revokingInvite.id, {
      // Close on both paths: on success the invite is gone; on failure the
      // hook refreshes the list (e.g. it was already accepted), so the dialog
      // no longer applies.
      onSettled: () => setRevokingInvite(null),
    });
  };

  // ── Cross-tab navigation flash ────────────────────────────────────
  const [flashUserId, setFlashUserId] = useState<string | null>(null);
  const [flashRoleId, setFlashRoleId] = useState<string | null>(null);
  const [flashOrgId, setFlashOrgId] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const orgsScrollRef = useRef<HTMLDivElement>(null);
  // Prevents the tab-change cleanup effect from clearing the drawer opened by a jump
  const isJumping = useRef(false);

  // Cross-tab jumps wrap the heavy bits (tab + filter reset, which mount the
  // target table) in startTransition so the click feels instant. The flash
  // markers and isJumping ref stay synchronous so the post-tab-change effect
  // and scroll-to-row logic still see them on the same render pass.
  const jumpToPerson = (userId: string) => {
    clearTimeout(flashTimer.current);
    isJumping.current = true;
    setActiveRoleId(null);
    setActiveUserId(userId);
    setFlashUserId(userId);
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: null, noRole: false, neverSignedIn: false, hasPendingInvite: false });
    });
    flashTimer.current = setTimeout(() => setFlashUserId(null), 1500);
  };

  const jumpToOrg = (tenantId: string) => {
    clearTimeout(flashTimer.current);
    isJumping.current = true;
    setActiveUserId(null);
    setActiveRoleId(null);
    setActiveOrgId(tenantId);
    setFlashOrgId(tenantId);
    startTransition(() => {
      setActiveTab("orgs");
      setOrgFilters({ search: "", industry: null, size: null, showArchived: false, excludedColors: [] });
    });
    flashTimer.current = setTimeout(() => setFlashOrgId(null), 1500);
  };

  const openNewRoleForTenant = (tenantId: string) => {
    isJumping.current = true;
    setActiveUserId(null);
    setShowNewRoleDialog(true);
    startTransition(() => {
      setActiveTab("roles");
      setRoleFilters({ search: "", orgId: tenantId, type: null, showArchived: false, onlyMine: false });
    });
  };

  // Open the new-role dialog pre-selected to a tenant *without* navigating away
  // from the current tab. Used by OrgDrawer's "Create first role" empty state
  // so the drawer stays open — once the role is created the cache invalidates,
  // groupsByTenant updates, and the drawer's CTA flips to "Add member" inline.
  const openNewRoleDialogForTenant = useCallback((tenantId: string) => {
    setRoleFilters((prev) => ({ ...prev, orgId: tenantId }));
    setShowNewRoleDialog(true);
  }, []);

  const jumpToRole = (accessId: string) => {
    clearTimeout(flashTimer.current);
    isJumping.current = true;
    setActiveUserId(null);
    setActiveRoleId(accessId);
    setFlashRoleId(accessId);
    startTransition(() => {
      setActiveTab("roles");
      setRoleFilters({ search: "", orgId: null, type: null, showArchived: false, onlyMine: false });
    });
    flashTimer.current = setTimeout(() => setFlashRoleId(null), 1500);
  };
  const [showNewRoleDialog, setShowNewRoleDialog] = useState(false);
  const [showNewOrgDialog, setShowNewOrgDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [auditLogState, setAuditLogState] = useState<{ open: boolean; highlightId: string | null; searchPrefill: string | null; category: AuditLogModalProps["category"] }>({ open: false, highlightId: null, searchPrefill: null, category: null });
  // Opening the audit modal mounts 100+ rows + Radix portal; wrapping in
  // startTransition lets the browser paint the button click first, then
  // renders the dialog content as an interruptible lower-priority update.
  //
  // `highlightId` scrolls to a specific entry (used by AuditTimeline rows in
  // drawers); `searchPrefill` pre-populates the search box so per-row "..."
  // menus land on a filtered view of that user/invite/role;
  // `category` pre-selects the action-category filter chip.
  const openAuditLog = useCallback(
    // `category` is widened to `string | null` so child components (PeopleTable,
    // InvitesTable, drawers) — which don't import the `ActionCategory` enum —
    // can wire this callback directly. The modal itself validates the value.
    (options: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null } = {}) => {
      startTransition(() =>
        setAuditLogState({
          open: true,
          highlightId:   options.highlightId   ?? null,
          searchPrefill: options.searchPrefill  ?? null,
          category:      (options.category ?? null) as AuditLogModalProps["category"],
        }),
      );
    },
    [],
  );
  // Closing is kept synchronous so Radix can start the exit animation
  // without delay (forceMount keeps rows in DOM so there's no unmount cost).
  const handleAuditLogChange = useCallback((open: boolean) => {
    setAuditLogState({ open, highlightId: null, searchPrefill: null, category: null });
  }, []);
  const [showBulkGrant, setShowBulkGrant] = useState(false);
  const [bulkGrantPending, setBulkGrantPending] = useState(false);
  const qc = useQueryClient();
  const reactivateUser = useReactivateUser();
  const revokeAccess = useRevokeAccess();
  const grantAccess = useGrantAccess();
  const deleteRole = useDeleteAccessGroup();
  const bulkRevokeAccess = useBulkRevokeAccess();
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [suspendingUser, setSuspendingUser] = useState<UserItem | null>(null);
  const [deletingRole, setDeletingRole] = useState<import("./RolesTable").RoleRow | null>(null);
  const [editingPermissionsRole, setEditingPermissionsRole] = useState<import("./RolesTable").RoleRow | null>(null);

  const handleUnsuspend = (u: UserItem) => {
    if (!activeTenantId) return;
    reactivateUser.mutate({ userId: u.id, tenantId: activeTenantId });
  };

  const handleRemoveFromRole = (userId: string) => {
    if (!activeRole) return;
    revokeAccess.mutate({ userId, accessId: activeRole.id, tenantId: activeRole.tenantId });
  };

  const handleAddMemberToRole = (userId: string) => {
    if (!activeRole) return;
    grantAccess.mutate({ userId, accessId: activeRole.id, tenantId: activeRole.tenantId });
  };

  const handleRemoveFromOrg = (userId: string, orgId: string) => {
    const entries = (accessByUser.get(userId) ?? []).filter((e) => e.access.tenantId === orgId);
    if (entries.length === 0) return;
    bulkRevokeAccess.mutate({
      userId,
      revocations: entries.map((e) => ({ accessId: e.accessId, tenantId: orgId })),
      tenantId: orgId,
    });
  };

  const handleBulkGrant = async (grants: Array<{ userId: string; accessId: string; tenantId: string }>) => {
    setBulkGrantPending(true);
    try {
      await Promise.all(grants.map((g) => grantUserAccess(g)));
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: identityHubKeys.users() });
        void qc.invalidateQueries({ queryKey: identityHubKeys.all });
      }, 1500);
      toast.success(t("iam.toast.bulkGrantSuccess", { count: grants.length }));
      setShowBulkGrant(false);
      setSelected(new Set());
    } catch (err) {
      Sentry.captureException(err);
      toast.error(t("iam.toast.bulkGrantFailed", "Some grants failed. Please try again."));
    } finally {
      setBulkGrantPending(false);
    }
  };

  const handleExportCsv = (scope: "selected" | "all" = "selected") => {
    const rowUsers = scope === "selected" && selected.size > 0
      ? users.filter((u) => selected.has(u.id))
      : users;

    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const header = [
      t("iam.csvExport.headers.name", "Name"),
      t("iam.csvExport.headers.email", "Email"),
      t("iam.csvExport.headers.status", "Status"),
      t("iam.csvExport.headers.organizations", "Organizations"),
      t("iam.csvExport.headers.primaryRole", "Primary Role"),
      t("iam.csvExport.headers.authProvider", "Auth Provider"),
      t("iam.csvExport.headers.joined", "Joined"),
      t("iam.csvExport.headers.lastActive", "Last Active"),
    ];
    const rows = rowUsers.map((u) => {
      const entries = accessByUser.get(u.id) ?? [];
      const orgs = [...new Set(entries.map((e) => e.access.tenant.name))].join("; ");
      const role = entries[0]?.access.name ?? "";
      return [
        u.displayName,
        u.email,
        u.status,
        orgs,
        role,
        u.authProvider ?? "",
        new Date(u.createdAt).toLocaleDateString(),
        u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleDateString() : "",
      ].map(escape).join(",");
    });

    const csv = [header.map(escape).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `people-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onOrgFilterChange = <K extends keyof OrgFilters>(key: K, value: OrgFilters[K]) => {
    setOrgFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ── Roles derived data ────────────────────────────────────────────
  const membersByGroup = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const [userId, entries] of accessByUser) {
      for (const entry of entries) {
        if (!map.has(entry.accessId)) map.set(entry.accessId, new Set());
        map.get(entry.accessId)!.add(userId);
      }
    }
    return map;
  }, [accessByUser]);

  const currentUserRoleIds = useMemo(
    () => (currentUserId ? new Set((accessByUser.get(currentUserId) ?? []).map((entry) => entry.accessId)) : new Set<string>()),
    [accessByUser, currentUserId],
  );

  const emptyRolesCount = useMemo(
    () => allAccessGroups.filter((g) => (membersByGroup.get(g.id)?.size ?? 0) === 0).length,
    [allAccessGroups, membersByGroup],
  );

  // For enterprise (non-super-tenant) admins, /access/all is inaccessible (403).
  // Derive role rows from the per-tenant access data already fetched via groupsByTenant.
  const nonSuperTenantRoleRows = useMemo<import("./RolesTable").RoleRow[]>(() => {
    if (isSuperTenant) return [];
    const rows: import("./RolesTable").RoleRow[] = [];
    for (const [tenantId, groups] of groupsByTenant) {
      const tenant = tenants.find((t) => t.id === tenantId);
      if (!tenant) continue;
      for (const g of groups) {
        if (roleFilters.showArchived === false && g.deletedAt) continue;
        rows.push({
          id: g.id,
          name: g.name,
          description: g.description ?? null,
          tenantId: g.tenantId,
          tenantName: tenant.name,
          tenantType: tenant.type === "SUPER_TENANT"
            ? "SUPER_TENANT"
            : tenant.type === "API"
              ? "API"
              : "ORGANIZATION",
          tenantLogoUrl: tenant.logoUrl ?? null,
          tenantBrandColor: tenant.brandColor ?? null,
          // Prefer the server aggregate; membersByGroup only covers currently
          // loaded users and would undercount roles with members off-page.
          memberCount: g.memberCount ?? membersByGroup.get(g.id)?.size ?? 0,
          permissionCount: g.permissions.length,
          type: g.type === "SYSTEM" ? "system" : "custom",
          updatedAt: g.updatedAt,
          isArchived: !!g.deletedAt,
        });
      }
    }
    return rows;
  }, [isSuperTenant, groupsByTenant, tenants, membersByGroup, roleFilters.showArchived]);

  const roleRows = useMemo(() => {
    const baseRows = isSuperTenant ? roleRowsFromApi : nonSuperTenantRoleRows;
    const filteredRows = roleFilters.onlyMine
      ? baseRows.filter((row) => currentUserRoleIds.has(row.id))
      : baseRows;
    return sortRoleRows(filteredRows, roleSort);
  }, [currentUserRoleIds, isSuperTenant, nonSuperTenantRoleRows, roleFilters.onlyMine, roleRowsFromApi, roleSort]);

  const activeRole = useMemo(
    () => roleRows.find((r) => r.id === activeRoleId) ?? null,
    [roleRows, activeRoleId],
  );

  const activeRolePermissions = useMemo(() => {
    if (!activeRoleId) return [];
    for (const groups of groupsByTenant.values()) {
      const group = groups.find((g) => g.id === activeRoleId);
      if (group) return group.permissions ?? [];
    }
    return [];
  }, [activeRoleId, groupsByTenant]);

  const superTenantIds = useMemo(
    () => new Set(tenants.filter((t) => t.type === "SUPER_TENANT").map((t) => t.id)),
    [tenants],
  );

  const membersByTenant = useMemo(() => {
    const map = new Map<string, typeof users>();
    for (const [userId, entries] of accessByUser) {
      const user = users.find((u) => u.id === userId);
      if (!user) continue;
      const seen = new Set<string>();
      for (const entry of entries) {
        const tid = entry.access.tenantId;
        if (seen.has(tid)) continue;
        seen.add(tid);
        if (!map.has(tid)) map.set(tid, []);
        map.get(tid)!.push(user);
      }
    }
    return map;
  }, [accessByUser, users]);

  const uniqueIndustries = useMemo(
    () => Array.from(new Set(tenants.map((t) => industryLabel(t.type)))).sort(),
    [tenants],
  );

  // ── KPI insights — temporarily disabled alongside KPI strip ──────
  // const kpiInsights = useMemo(() => {
  //   // Largest org by member count.
  //   let topOrg: { name: string; members: number } | null = null;
  //   for (const [tid, members] of membersByTenant) {
  //     if (!topOrg || members.length > topOrg.members) {
  //       const tenant = tenants.find((t) => t.id === tid);
  //       if (tenant) topOrg = { name: tenant.name, members: members.length };
  //     }
  //   }
  //
  //   // Most-populated access role.
  //   let topRole: { name: string; members: number } | null = null;
  //   for (const [accessId, memberIds] of membersByGroup) {
  //     const size = memberIds.size;
  //     if (!topRole || size > topRole.members) {
  //       for (const groups of groupsByTenant.values()) {
  //         const g = groups.find((g) => g.id === accessId);
  //         if (g) { topRole = { name: g.name, members: size }; break; }
  //       }
  //     }
  //   }
  //
  //   // Cross-org users (multi-tenant accounts) — straightforward signal of
  //   // access sprawl; useful "fun" stat.
  //   let crossOrgUsers = 0;
  //   for (const entries of accessByUser.values()) {
  //     const tenantsForUser = new Set(entries.map((e) => e.access.tenantId));
  //     if (tenantsForUser.size > 1) crossOrgUsers++;
  //   }
  //
  //   // Newest account — show as relative time string.
  //   const newestAccount = users
  //     .filter((u) => u.createdAt)
  //     .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  //   const newestAccountAgo = newestAccount ? formatRelativeShort(newestAccount.createdAt) : null;
  //
  //   // Invite acceptance rate + avg time to accept (lifetime).
  //   let acceptanceRate: number | null = null;
  //   let avgAcceptDays: number | null = null;
  //   let invitesSent7d: number | null = null;
  //   if (allInvites.length > 0) {
  //     const accepted = allInvites.filter((i) => i.status === "accepted");
  //     acceptanceRate = Math.round((accepted.length / allInvites.length) * 100);
  //
  //     if (accepted.length > 0) {
  //       const totalAcceptMs = accepted.reduce((sum, i) => {
  //         if (!i.usedAt) return sum;
  //         return sum + (new Date(i.usedAt).getTime() - new Date(i.createdAt).getTime());
  //       }, 0);
  //       const avgDays = totalAcceptMs / accepted.length / 86_400_000;
  //       avgAcceptDays = avgDays >= 1 ? Math.round(avgDays) : Math.max(1, Math.round(avgDays * 10) / 10);
  //     }
  //
  //     const since7d = Date.now() - 7 * 86_400_000;
  //     invitesSent7d = allInvites.filter((i) => new Date(i.createdAt).getTime() >= since7d).length;
  //   }
  //
  //   return { topOrg, topRole, crossOrgUsers, newestAccountAgo, acceptanceRate, avgAcceptDays, invitesSent7d };
  // }, [membersByTenant, tenants, membersByGroup, groupsByTenant, accessByUser, users, allInvites]);

  // Reset selection + drawers when tab changes (skip when tab change is from a jump navigation)
  useEffect(() => {
    if (isJumping.current) {
      isJumping.current = false;
      return;
    }
    setSelected(new Set());
    setActiveUserId(null);
    setActiveOrgId(null);
    setActiveRoleId(null);
  }, [activeTab]);

  const onFilterChange = useCallback(<K extends keyof PeopleFilters>(key: K, value: PeopleFilters[K]) => {
    setPeopleFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onSort = useCallback((key: SortKey) => {
    setPeopleSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }, []);

  const onOrgSort = useCallback((key: OrgSortKey) => {
    setOrgSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }, []);

  const onRoleSort = useCallback((key: RoleSortKey) => {
    setRoleSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }, []);

  const onToggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const onToggleAll = useCallback((ids: string[]) => {
    setSelected((prev) =>
      ids.every((id) => prev.has(id)) ? new Set() : new Set(ids),
    );
  }, []);

  // ── Stable PeopleTable row callbacks ──────────────────────────────
  // Pinned identity so the memoized `PersonRow` (React.memo) can skip
  // re-rendering rows on dashboard state changes that don't touch them.
  const onPersonRowClick = useCallback((id: string) => {
    setActiveUserId((prev) => (prev === id ? null : id));
  }, []);
  const onPersonEdit = useCallback((u: UserItem) => setEditingUser(u), []);
  const onPersonSuspend = useCallback((u: UserItem) => setSuspendingUser(u), []);
  const onPersonUnsuspend = useCallback((u: UserItem) => {
    if (!activeTenantId) return;
    reactivateUser.mutate({ userId: u.id, tenantId: activeTenantId });
  }, [activeTenantId, reactivateUser]);
  // Copies the invite's EXISTING link via GET /invites/:id/link (read-only) —
  // it must not regenerate, which would expire the invitee's emailed link
  // (AD-1714). Only reachable when the person has a pending invite.
  const onPersonCopyInviteLink = useCallback((u: UserItem, _accessId?: string) => {
    const inviteId = u.pendingInvite?.inviteId;
    if (!inviteId) return;
    inviteLinkMut.mutate(inviteId, {
      onSuccess: (data) => { void copyInviteLinkToClipboard(data.token); },
    });
  }, [inviteLinkMut, copyInviteLinkToClipboard]);

  // Unique roles from loaded access data for the filter chip
  const availableRoles = useMemo(() => {
    const names = new Set<string>();
    for (const entries of accessByUser.values()) {
      for (const e of entries) names.add(e.access.name);
    }
    return Array.from(names).sort();
  }, [accessByUser]);

  // ── Tab options ───────────────────────────────────────────────────
  const tabOptions = useMemo(
    () => TAB_OPTION_VALUES.map((value) => ({
      value,
      label: t(`iam.dashboard.tabs.${value}`),
      badge:
        value === "people"  ? peopleTotal :
        value === "roles"   ? rolesTotal :
        value === "orgs"    ? orgsTotal :
        value === "invites" ? (inviteStatusCounts.all || undefined) :
        undefined,
    })),
    [t, peopleTotal, rolesTotal, orgsTotal, invitesPendingTotal],
  );

  // ── Scroll containers for virtualized tables ─────────────────────
  // Each tab's table virtualizes its rows against the scroller of that
  // tab's content area, so only ~15 visible rows mount instead of all N.
  const peopleScrollRef = useRef<HTMLDivElement>(null);
  const rolesScrollRef = useRef<HTMLDivElement>(null);
  const invitesScrollRef = useRef<HTMLDivElement>(null);
  const peopleSentinelRef = useRef<HTMLDivElement>(null);
  const rolesSentinelRef = useRef<HTMLDivElement>(null);
  const orgsSentinelRef = useRef<HTMLDivElement>(null);
  const invitesSentinelRef = useRef<HTMLDivElement>(null);

  useInfiniteScroll(
    peopleSentinelRef,
    peopleScrollRef,
    () => { void peopleQuery.fetchNextPage(); },
    !!peopleQuery.hasNextPage,
    peopleQuery.isFetchingNextPage,
    { enabled: activeTab === "people", recheckKey: users.length },
  );
  useInfiniteScroll(
    orgsSentinelRef,
    orgsScrollRef,
    () => { void tenantsQuery.fetchNextPage(); },
    !!tenantsQuery.hasNextPage,
    tenantsQuery.isFetchingNextPage,
    { enabled: activeTab === "orgs", recheckKey: `${allTenants.length}:${filteredTenants.length}` },
  );
  useInfiniteScroll(
    invitesSentinelRef,
    invitesScrollRef,
    () => { void invitesQuery.fetchNextPage(); },
    !!invitesQuery.hasNextPage,
    invitesQuery.isFetchingNextPage,
    { enabled: activeTab === "invites" && canViewInvites, recheckKey: allInvites.length },
  );
  useInfiniteScroll(
    rolesSentinelRef,
    rolesScrollRef,
    () => { void rolesQuery.fetchNextPage(); },
    !!rolesQuery.hasNextPage,
    rolesQuery.isFetchingNextPage,
    { enabled: activeTab === "roles", recheckKey: roleRowsFromApi.length },
  );

  // ── Stable toolbar/dialog inputs ──────────────────────────────────
  // Without these `useMemo`s, `tenants.map(...)` was rebuilt every render
  // and handed to PeopleToolbar / RolesToolbar / InvitesToolbar —
  // invalidating their entire prop trees on every click anywhere on the dashboard.
  const orgsForToolbar = useMemo(
    () => tenants.map((t) => ({
      id: t.id,
      name: t.name,
      type: t.type,
      logoUrl: t.logoUrl,
      brandColor: t.brandColor,
    })),
    [tenants],
  );
  // Invite / role / grant / person-drawer pickers must not reuse the Orgs-tab
  // infinite list: that query is the first page (or the current search), so a
  // tenant like Vodafone is missing until someone filters for it on Organizations.
  // Do not fall back to `tenants` while the picker is loading — that truncated
  // list is the bug (AD-2335).
  const orgsForPickers = useMemo(() => {
    const source = (tenantPickerQuery.data?.items ?? []).filter((t) => !t.deletedAt);
    return source.map((t) => ({
      id: t.id,
      name: t.name,
      type: t.type,
      logoUrl: t.logoUrl,
      brandColor: t.brandColor,
    }));
  }, [tenantPickerQuery.data]);

  // ── KPI strip click handlers ──────────────────────────────────────
  // Stable identities so the memoized `IamKpiStrip` (and its 8 memoized
  // KpiCards) can skip re-renders on every dashboard state change.
  // Switching tabs synchronously mounts a new table (people / orgs / roles /
  // invites) with up to hundreds of rows, so each setActiveTab call is
  // wrapped in startTransition — React paints the click feedback first and
  // renders the new tab as an interruptible low-priority update.
  const onKpiPeopleClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: null, noRole: false, neverSignedIn: false, hasPendingInvite: false });
    });
  }, [setActiveTab]);
  const onKpiOrgsClick = useCallback(() => {
    startTransition(() => setActiveTab("orgs"));
  }, [setActiveTab]);
  const onKpiPendingClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("invites");
      setInvitesFilters({ search: "", status: "pending", tenantId: null });
    });
  }, [setActiveTab]);
  const onKpiRolesClick = useCallback(() => {
    startTransition(() => setActiveTab("roles"));
  }, [setActiveTab]);
  const onKpiDeactivatedClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: "DEACTIVATED", noRole: false, neverSignedIn: false, hasPendingInvite: false });
    });
  }, [setActiveTab]);
  const onKpiNeverLoggedInClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: null, noRole: false, neverSignedIn: true, hasPendingInvite: false });
    });
  }, [setActiveTab]);
  const onKpiRolelessClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: null, noRole: true, neverSignedIn: false, hasPendingInvite: false });
    });
  }, [setActiveTab]);

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-3 sm:gap-5 sm:p-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-xl font-semibold leading-tight">{t("iam.dashboard.pageTitle")}</h1>
          <p className="text-sm text-muted-foreground">{t("iam.dashboard.pageDescription")}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canViewAuditLog && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openAuditLog()}>
              <Clock className="h-3.5 w-3.5" />
              {t("iam.dashboard.auditLog")}
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip — temporarily hidden; preserved for Storybook / future re-enable */}
      {/* <IamKpiStrip
        accountsTotal={stats?.people.current ?? users.length}
        orgsTotal={stats?.orgs.current ?? tenants.length}
        orgsInactive={stats?.orgs.totalInactive ?? 0}
        privilegedTotal={stats?.privileged.current ?? 0}
        pendingTotal={stats?.pending.current ?? inviteStatusCounts.pending}
        peopleDelta={stats?.people.delta ?? 0}
        orgsDelta={stats?.orgs.delta ?? 0}
        privilegedDelta={stats?.privileged.delta ?? 0}
        pendingDelta={stats?.pending.delta ?? 0}
        adminGroupCount={adminGroupCount}
        invitedCount={pendingUsers.length}
        activeCount={activeUsers.length}
        oldestPendingDays={stats?.pending.oldestDays ?? null}
        rolesTotal={totalGroups}
        emptyRolesCount={emptyRolesCount}
        deactivatedTotal={deactivatedUsers.length}
        neverLoggedIn={neverLoggedIn}
        rolelessCount={rolelessCount}
        statsWindow={IAM_STATS_WINDOW}
        loading={loading || statsLoading}
        insights={kpiInsights}
        onPeopleClick={onKpiPeopleClick}
        onOrgsClick={onKpiOrgsClick}
        onPendingClick={onKpiPendingClick}
        onRolesClick={onKpiRolesClick}
        onDeactivatedClick={onKpiDeactivatedClick}
        onNeverLoggedInClick={onKpiNeverLoggedInClick}
        onRolelessClick={onKpiRolelessClick}
      /> */}

      {/* Tab bar — top level, outside any card */}
      <div className="overflow-x-auto border-b border-border">
        <Tabs value={activeTab} onValueChange={(v) => startTransition(() => setActiveTab(v as IamDashTab))}>
          <TabsList
            variant="line"
            options={tabOptions}
            className="h-11 min-w-max rounded-none bg-transparent p-0"
          />
        </Tabs>
      </div>

      {/* Content area — split-pane: [main column | drawer] */}
      <div className="relative flex min-h-0 flex-1 gap-3 overflow-hidden">
        {/* Left column */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">

          {/* People tab */}
          {activeTab === "people" && (
            <>
              <PeopleToolbar
                filters={peopleFilters}
                onFilterChange={onFilterChange}
                selectedCount={selected.size}
                onClearSelection={() => setSelected(new Set())}
                onInvite={() => setShowInviteDialog(true)}
                onBulkAssignRole={() => setShowBulkGrant(true)}
                onBulkExport={() => handleExportCsv("selected")}
                orgs={orgsForToolbar}
                roles={availableRoles}
                canInvite={canInvite}
              />
              <div ref={peopleScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex flex-col gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full rounded-md" />
                    ))}
                  </div>
                ) : (
                  <>
                    <PeopleTable
                      users={users}
                      accessByUser={accessByUser}
                      sort={peopleSort}
                      onSort={onSort}
                      selected={selected}
                      onToggleRow={onToggleRow}
                      onToggleAll={onToggleAll}
                      activeId={activeUserId}
                      onRowClick={onPersonRowClick}
                      onEditProfile={onPersonEdit}
                      onSuspend={onPersonSuspend}
                      onUnsuspend={onPersonUnsuspend}
                      onOpenAuditLog={canViewAuditLog ? openAuditLog : undefined}
                      onCopyInviteLink={canCopyInviteLink ? onPersonCopyInviteLink : undefined}
                      flashId={flashUserId}
                      scrollContainerRef={peopleScrollRef}
                      hasMore={peopleQuery.hasNextPage}
                      isFetchingNextPage={peopleQuery.isFetchingNextPage}
                    />
                    <div ref={peopleSentinelRef} className="h-px" aria-hidden />
                    {peopleQuery.isFetchingNextPage && (
                      <div className="flex justify-center py-3 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Orgs tab */}
          {activeTab === "orgs" && (
            <>
              <OrgToolbar
                filters={orgFilters}
                onFilterChange={onOrgFilterChange}
                industries={uniqueIndustries}
                availableColors={availableColors}
                onNewOrg={() => setShowNewOrgDialog(true)}
                canCreate={canNewOrg}
                archivedCount={archivedTenantCount}
              />
              <div ref={orgsScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex flex-col gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full rounded-md" />
                    ))}
                  </div>
                ) : (
                  <OrgsTable
                    tenants={colorFilteredTenants}
                    membersByTenant={membersByTenant}
                    groupsByTenant={groupsByTenant}
                    superTenantIds={superTenantIds}
                    sort={orgSort}
                    onSort={onOrgSort}
                    activeId={activeOrgId}
                    onRowClick={(id) => setActiveOrgId((prev) => (prev === id ? null : id))}
                    onOpenSettings={(id) => setSettingsOrgId(id)}
                    onOpenAuditLog={
                      canViewAuditLog ? (_id, name) => openAuditLog({ searchPrefill: name }) : undefined
                    }
                    flashId={flashOrgId}
                    scrollContainerRef={orgsScrollRef}
                    hasMore={tenantsQuery.hasNextPage}
                    isFetchingNextPage={tenantsQuery.isFetchingNextPage}
                  />
                )}
                {!loading && (
                  <>
                    <div ref={orgsSentinelRef} className="h-px" aria-hidden />
                    {tenantsQuery.isFetchingNextPage && (
                      <div className="flex justify-center py-3 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Roles tab */}
          {activeTab === "roles" && (
            <>
              <RolesToolbar
                filters={roleFilters}
                onFilterChange={(key, value) => setRoleFilters((prev) => ({ ...prev, [key]: value }))}
                orgs={orgsForToolbar}
                onNewRole={() => setShowNewRoleDialog(true)}
                canCreate={canNewRole}
                archivedCount={archivedRoleCount}
              />
              <div ref={rolesScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                {rolesQuery.isLoading && roleRows.length === 0 ? (
                  <div className="flex flex-col gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full rounded-md" />
                    ))}
                  </div>
                ) : (
                  <>
                    <RolesTable
                      rows={roleRows}
                      sort={roleSort}
                      onSort={onRoleSort}
                      activeId={activeRoleId}
                      onRowClick={(id) => setActiveRoleId((prev) => (prev === id ? null : id))}
                      onEditPermissions={canEditPermissions ? setEditingPermissionsRole : undefined}
                      onDeleteRole={canDeleteRole ? setDeletingRole : undefined}
                      onOpenAuditLog={
                        canViewAuditLog
                          ? (role) => openAuditLog({ searchPrefill: `${role.name} ${role.tenantName}`, category: "access" })
                          : undefined
                      }
                      flashId={flashRoleId}
                      scrollContainerRef={rolesScrollRef}
                      hasMore={rolesQuery.hasNextPage}
                      isFetchingNextPage={rolesQuery.isFetchingNextPage}
                      totalCount={rolesTotal}
                    />
                    <div ref={rolesSentinelRef} className="h-px" aria-hidden />
                  </>
                )}
              </div>
            </>
          )}

          {/* Invites tab */}
          {activeTab === "invites" && (
            <>
              <InvitesToolbar
                filters={invitesFilters}
                onFilterChange={onInvitesFilterChange}
                orgs={orgsForToolbar}
                onInvite={() => setShowInviteDialog(true)}
                canInvite={canInvite}
                counts={inviteStatusCounts}
              />
              <div ref={invitesScrollRef} className="min-h-0 flex-1 overflow-y-auto pb-2">
                {invitesQuery.isLoading && allInvites.length === 0 ? (
                  <div className="flex flex-col gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full rounded-md" />
                    ))}
                  </div>
                ) : (
                  <>
                    <InvitesTable
                      invites={sortedInvites}
                      loading={invitesQuery.isFetching && allInvites.length === 0}
                      orgLogosByTenantId={orgLogosByTenantId}
                      onCopyLink={canCopyInviteLink ? handleCopyInviteLink : undefined}
                      onResend={canResendInvite ? handleResendInvite : undefined}
                      onRegenerate={canRegenerateInvite ? handleRegenerateInvite : undefined}
                      onRevoke={canRevokeInvite ? (invite) => setRevokingInvite(invite) : undefined}
                      onOpenAuditLog={canViewAuditLog ? openAuditLog : undefined}
                      busyInviteId={busyInviteId}
                      sort={invitesSort}
                      onSort={onInvitesSort}
                      scrollContainerRef={invitesScrollRef}
                    />
                    <div ref={invitesSentinelRef} className="h-px" aria-hidden />
                    {invitesQuery.isFetchingNextPage && (
                      <div className="flex justify-center py-3 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Drawer — People tab */}
        {activeTab === "people" && activeUserId && (
          <PersonDrawer
            user={users.find((u) => u.id === activeUserId) ?? null}
            access={accessByUser.get(activeUserId) ?? []}
            allTenants={orgsForPickers}
            accessGroupsByTenant={groupsByTenant}
            onClose={() => setActiveUserId(null)}
            activeTenantId={activeTenantId}
            loggedInEmail={loggedInEmail}
            onEditProfile={() => {
              const u = users.find((u) => u.id === activeUserId);
              if (u) setEditingUser(u);
            }}
            onSuspend={() => {
              const u = users.find((u) => u.id === activeUserId);
              if (u) setSuspendingUser(u);
            }}
            onUnsuspend={() => {
              const u = users.find((u) => u.id === activeUserId);
              if (u) handleUnsuspend(u);
            }}
            onJumpToRole={jumpToRole}
            onJumpToOrg={jumpToOrg}
            onCreateRole={openNewRoleForTenant}
            onOpenAuditLog={openAuditLog}
          />
        )}

        {/* Drawer — Orgs tab */}
        {activeTab === "orgs" && activeOrgId && (
          <OrgDrawer
            org={allTenants.find((t) => t.id === activeOrgId) ?? null}
            members={membersByTenant.get(activeOrgId) ?? []}
            roles={(groupsByTenant.get(activeOrgId) ?? []).filter((r) => !r.deletedAt)}
            accessByUser={accessByUser}
            allUsers={users}
            isSuperTenant={superTenantIds.has(activeOrgId)}
            onClose={() => setActiveOrgId(null)}
            onJumpToPerson={(userId) => {
              setActiveOrgId(null);
              jumpToPerson(userId);
            }}
            onJumpToRole={(roleId) => {
              setActiveOrgId(null);
              jumpToRole(roleId);
            }}
            onRemoveFromOrg={(userId) => handleRemoveFromOrg(userId, activeOrgId)}
            onRemoveRole={(roleId) => {
              const role = roleRows.find((r) => r.id === roleId);
              if (role) setDeletingRole(role);
            }}
            onEditProfile={(u) => setEditingUser(u)}
            onSuspend={(u) => setSuspendingUser(u)}
            onUnsuspend={(u) => handleUnsuspend(u)}
            onAddMember={(userId, accessId) =>
              grantAccess.mutate({ userId, accessId, tenantId: activeOrgId })
            }
            isAddingMember={grantAccess.isPending}
            addingMemberId={grantAccess.isPending ? grantAccess.variables?.userId ?? null : null}
            addingAccessId={grantAccess.isPending ? grantAccess.variables?.accessId ?? null : null}
            isRemovingMember={bulkRevokeAccess.isPending}
            removingMemberId={bulkRevokeAccess.isPending ? bulkRevokeAccess.variables?.userId ?? null : null}
            onCreateRole={canNewRole ? openNewRoleDialogForTenant : undefined}
            onOpenSettings={(tenantId) => setSettingsOrgId(tenantId)}
            onOpenAuditLog={openAuditLog}
          />
        )}

        {/* Org settings modal (centered) — rendered only while open so every open starts fresh */}
        {settingsOrgId && (() => {
          const settingsOrg = allTenants.find((t) => t.id === settingsOrgId) ?? null;
          return settingsOrg ? (
            <OrgSettingsModal
              org={settingsOrg}
              isSuperTenant={superTenantIds.has(settingsOrgId)}
              onClose={() => setSettingsOrgId(null)}
            />
          ) : null;
        })()}
        {activeTab === "roles" && activeRoleId && (
          <RoleDrawer
            role={activeRole}
            members={activeRoleMembers}
            membersLoading={roleMembersQuery.isLoading}
            hasMoreMembers={roleMembersQuery.hasNextPage}
            isFetchingMoreMembers={roleMembersQuery.isFetchingNextPage}
            onLoadMoreMembers={() => { void roleMembersQuery.fetchNextPage(); }}
            permissions={activeRolePermissions}
            allUsers={users}
            accessByUser={accessByUser}
            onClose={() => setActiveRoleId(null)}
            onJumpToPerson={jumpToPerson}
            onRemoveMember={handleRemoveFromRole}
            onAddMember={handleAddMemberToRole}
            isAddingMember={grantAccess.isPending}
            addingMemberId={grantAccess.isPending ? grantAccess.variables?.userId ?? null : null}
            isRemovingMember={revokeAccess.isPending}
            removingMemberId={revokeAccess.isPending ? revokeAccess.variables?.userId ?? null : null}
            onEditProfile={(u) => setEditingUser(u)}
            onSuspend={(u) => setSuspendingUser(u)}
            onUnsuspend={(u) => handleUnsuspend(u)}
            onOpenAuditLog={openAuditLog}
          />
        )}
      </div>

      {/* New org dialog */}
      <NewOrgDialog
        open={showNewOrgDialog}
        onOpenChange={setShowNewOrgDialog}
        superTenantId={activeTenantId}
        allUsers={users}
        superTenantRoles={activeTenantId ? (groupsByTenant.get(activeTenantId) ?? []).filter((r) => !r.deletedAt) : []}
      />

      {/* Invite person dialog */}
      <InvitePersonDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        orgs={orgsForPickers}
        accessGroups={allAccessGroups}
      />

      {/* Audit log modal */}
      <AuditLogModal
        open={auditLogState.open}
        onOpenChange={handleAuditLogChange}
        highlightId={auditLogState.highlightId}
        searchPrefill={auditLogState.searchPrefill}
        category={auditLogState.category}
      />

      {/* Bulk grant dialog */}
      <BulkGrantDialog
        open={showBulkGrant}
        onOpenChange={setShowBulkGrant}
        selectedUserIds={Array.from(selected)}
        accessByUser={accessByUser}
        allTenants={orgsForPickers}
        groupsByTenant={groupsByTenant}
        onApply={handleBulkGrant}
        isPending={bulkGrantPending}
      />

      {/* Edit profile dialog */}
      <EditPersonDialog
        user={editingUser}
        open={!!editingUser}
        onOpenChange={(v) => { if (!v) setEditingUser(null); }}
        activeTenantId={activeTenantId}
      />

      {/* Suspend account dialog */}
      <SuspendPersonDialog
        user={suspendingUser}
        open={!!suspendingUser}
        onOpenChange={(v) => { if (!v) setSuspendingUser(null); }}
        activeTenantId={activeTenantId}
      />

      {/* New role dialog */}
      <NewRoleDialog
        open={showNewRoleDialog}
        onOpenChange={setShowNewRoleDialog}
        orgs={orgsForPickers}
        defaultOrgId={roleFilters.orgId}
        existingRoles={allAccessGroups}
      />

      {editingPermissionsRole && (
        <EditPermissionsDialog
          open={!!editingPermissionsRole}
          onOpenChange={(v) => { if (!v) setEditingPermissionsRole(null); }}
          role={editingPermissionsRole}
        />
      )}

      {/* Revoke invite confirmation dialog */}
      <Dialog
        open={!!revokingInvite}
        onOpenChange={(v) => { if (!v) setRevokingInvite(null); }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10">
              <XCircle className="h-4 w-4 text-destructive" />
            </div>
            <DialogTitle>{t("iam.dashboard.invites.revokeDialog.title", "Revoke invite?")}</DialogTitle>
            <DialogDescription>
              {revokingInvite && (
                <>
                  {t("iam.dashboard.invites.revokeDialog.body", "The invite link sent to ")}
                  <strong className="text-foreground">{revokingInvite.email}</strong>{" "}
                  {t("iam.dashboard.invites.revokeDialog.bodyTail", "will stop working immediately.")}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setRevokingInvite(null)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={revokeInviteMut.isPending}
              onClick={handleConfirmRevoke}
            >
              {revokeInviteMut.isPending
                ? t("iam.dashboard.invites.revokeDialog.pending", "Revoking…")
                : t("iam.dashboard.invites.revokeDialog.confirm", "Revoke invite")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete role confirmation dialog */}
      <Dialog open={!!deletingRole} onOpenChange={(v) => { if (!v) setDeletingRole(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10">
              <Trash2 className="h-4 w-4 text-destructive" />
            </div>
            <DialogTitle>{t("iam.dashboard.roles.deleteDialog.title", "Delete role?")}</DialogTitle>
            <DialogDescription>
              {deletingRole && (
                <>
                  <strong className="text-foreground">{deletingRole.name}</strong>
                  {t("iam.dashboard.roles.deleteDialog.bodyMiddle", " in ")}
                  <strong className="text-foreground">{deletingRole.tenantName}</strong>
                  {t("iam.dashboard.roles.deleteDialog.bodySuffix", " will be permanently removed.")}
                  {(membersByGroup.get(deletingRole.id)?.size ?? 0) > 0 && (
                    <span className="mt-1 block text-destructive">
                      {t("iam.dashboard.roles.deleteDialog.memberWarning", "{{count}} members will immediately lose access.", { count: membersByGroup.get(deletingRole.id)!.size })}
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDeletingRole(null)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteRole.isPending}
              onClick={() => {
                if (!deletingRole) return;
                deleteRole.mutate(
                  { accessId: deletingRole.id, tenantId: deletingRole.tenantId },
                  {
                    onSuccess: () => {
                      if (activeRoleId === deletingRole.id) setActiveRoleId(null);
                      setDeletingRole(null);
                    },
                  },
                );
              }}
            >
              {deleteRole.isPending ? t("iam.dashboard.roles.deleteDialog.pending", "Deleting…") : t("iam.dashboard.roles.deleteDialog.confirm", "Delete role")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
